import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { CheckCircle, XCircle, Info, ArrowRight, BookOpen, ArrowLeft, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { ExplanationWithLinks } from '../components/ExplanationWithLinks';
import AiLesson from '../components/AiLesson';
import { sanitizeHtml, sanitizeVideoUrl } from '../lib/sanitize';

export default function Training() {
    const [categories, setCategories] = useState(['all']);
    const [questions, setQuestions] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [feedback, setFeedback] = useState(null); // { is_correct, explanation, correct_option_id }
    const [selectedOption, setSelectedOption] = useState(null);
    const [selectedCourse, setSelectedCourse] = useState(null);

    const fetchQuestions = async () => {
        setLoading(true);
        try {
            let url = '/api/questions';
            const params = new URLSearchParams();
            if (!(categories.length === 1 && categories[0] === 'all')) {
                categories.forEach(c => params.append('category', c));
            }
            if (debouncedSearch && debouncedSearch.trim()) {
                params.append('q', debouncedSearch.trim());
            }
            const qs = params.toString();
            if (qs) url = `${url}?${qs}`;
            const res = await axios.get(url);
            // Shuffle client side for variety
            const shouldShuffle = !(debouncedSearch && debouncedSearch.trim());
            const data = shouldShuffle ? res.data.sort(() => 0.5 - Math.random()) : res.data;
            setQuestions(data);
            setCurrentIndex(0);
            setFeedback(null);
            setSelectedOption(null);
        } catch (e) {
            toast.error("Erreur lors du chargement des questions");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchQuestions();
    }, [categories.join(','), debouncedSearch]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(t);
    }, [search]);

    const handleAnswer = async (optionId) => {
        if (feedback) return; // Already answered
        setSelectedOption(optionId);

        try {
            const q = questions[currentIndex];
            const res = await axios.post('/api/training/check', {
                question_id: q.id,
                selected_option_id: optionId
            });
            setFeedback(res.data);
        } catch (e) {
            toast.error("Erreur de vérification");
        }
    };

    const nextQuestion = () => {
        if (currentIndex < questions.length - 1) {
            setCurrentIndex(prev => prev + 1);
            setFeedback(null);
            setSelectedOption(null);
        } else {
            // Restart or fetch more
            toast.info("Série terminée ! On recommence.");
            fetchQuestions();
        }
    };

    // Après une réponse, la touche Entrée passe à la question suivante (confort clavier).
    useEffect(() => {
        if (!feedback) return;
        const onKey = (e) => {
            if (e.key === 'Enter') nextQuestion();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [feedback, currentIndex, questions.length]);

    const handleCourseClick = async (courseId) => {
        try {
            const res = await axios.get(`/api/courses/${courseId}`);
            setSelectedCourse(res.data);
        } catch (e) {
            toast.error("Cours introuvable");
        }
    };

    if (loading && questions.length === 0) return <div className="p-8 text-center">Chargement...</div>;

    if (questions.length === 0) return (
        <div className="p-8 text-center">
            <p className="mb-4">Aucune question disponible dans cette catégorie.</p>
            <Button onClick={() => setCategories(['all'])}>Voir toutes les questions</Button>
        </div>
    );

    const currentQ = questions[currentIndex];

    return (
        <div className="min-h-screen pb-20">
            {/* En-tête cohérent avec le reste du site + retour accueil */}
            <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-30 p-4">
                <div className="max-w-3xl mx-auto flex items-center gap-3">
                    <Link to="/">
                        <Button variant="ghost" size="icon" aria-label="Retour à l'accueil">
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                    </Link>
                    <h1 className="text-xl font-heading font-bold text-slate-900 dark:text-white">Entraînement</h1>
                    <Link to="/mistakes" className="ml-auto">
                        <Button variant="outline" size="sm">
                            <BookOpen className="mr-2 h-4 w-4" /> Mes erreurs
                        </Button>
                    </Link>
                </div>
            </header>

            <main className="max-w-3xl mx-auto p-4">
                {/* Contrôles : recherche + catégories, aérés et responsives */}
                <div className="mb-6 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 shadow-sm">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Rechercher une question…"
                                aria-label="Rechercher une question"
                                className="w-full rounded-lg border pl-9 pr-9 py-2 text-sm bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/40"
                            />
                            {search && (
                                <button
                                    type="button"
                                    onClick={() => setSearch('')}
                                    aria-label="Effacer la recherche"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                        <Select
                            value=""
                            onValueChange={(val) => {
                                if (val === 'all') {
                                    setCategories(['all']);
                                } else {
                                    setCategories((prev) => {
                                        const base = prev.includes('all') ? [] : [...prev];
                                        return base.includes(val) ? base : [...base, val];
                                    });
                                }
                            }}
                        >
                            <SelectTrigger aria-label="Ajouter une catégorie">
                                <SelectValue placeholder="Ajouter une catégorie" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Toutes catégories</SelectItem>
                                <SelectItem value="Priorités">Priorités</SelectItem>
                                <SelectItem value="Croisements">Croisements</SelectItem>
                                <SelectItem value="Signalisations">Signalisations</SelectItem>
                                <SelectItem value="Mécanique">Mécanique</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Catégories&nbsp;:</span>
                        {categories.map((c) => (
                            <Badge key={c} variant="secondary" className="cursor-pointer gap-1" onClick={() => {
                                setCategories((prev) => {
                                    const next = prev.filter(x => x !== c);
                                    return next.length ? next : ['all'];
                                });
                            }}>
                                {c === 'all' ? 'Toutes' : c}
                                {c !== 'all' && <X className="h-3 w-3" />}
                            </Badge>
                        ))}
                    </div>
                </div>

            {/* Question Card */}
            <div>
                {/* Progression + rappel du fonctionnement */}
                <div className="mb-3 flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                        Question {currentIndex + 1} sur {questions.length}
                    </span>
                    <span className="text-xs text-slate-500 dark:text-slate-400 text-right">
                        Choisis une réponse pour voir la correction — en cas d'erreur, demande ta « petite leçon ».
                    </span>
                </div>
                <Card className="border-0 shadow-lg overflow-hidden bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                    {currentQ.image_url && (
                        <div className="h-64 bg-black flex items-center justify-center">
                            <img src={currentQ.image_url} alt="Context" className="h-full object-contain" />
                        </div>
                    )}
                    
                    <CardContent className="p-6 md:p-8">
                        <h2 className="text-xl font-semibold mb-8 text-slate-900 dark:text-white">{currentQ.text}</h2>

                        <div className="grid grid-cols-1 gap-3">
                            {currentQ.options.map(opt => {
                                let stateClass = "border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/30";
                                let icon = null;

                                if (feedback) {
                                    if (opt.id === feedback.correct_option_id) {
                                        stateClass = "border-sky-500 bg-sky-100 dark:bg-sky-950/30 text-sky-900 dark:text-sky-100 ring-1 ring-sky-500";
                                        icon = <CheckCircle className="h-5 w-5 text-sky-600 dark:text-sky-400 ml-auto" />;
                                    } else if (opt.id === selectedOption && opt.id !== feedback.correct_option_id) {
                                        stateClass = "border-red-500 bg-red-100 dark:bg-red-950/30 text-red-900 dark:text-red-100 ring-1 ring-red-500";
                                        icon = <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 ml-auto" />;
                                    } else {
                                        stateClass = "opacity-50";
                                    }
                                } else if (selectedOption === opt.id) {
                                    stateClass = "border-primary bg-primary/5 ring-1 ring-primary";
                                }

                                return (
                                    <div 
                                        key={opt.id}
                                        onClick={() => handleAnswer(opt.id)}
                                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex items-center ${stateClass}`}
                                    >
                                        <span className="font-medium">{opt.text}</span>
                                        {icon}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Feedback Section */}
                        {feedback && (
                            <div className={`mt-6 p-4 rounded-xl border animate-in slide-in-from-bottom-2 ${
                                feedback.is_correct 
                                    ? 'bg-sky-100 dark:bg-sky-950/30 border-sky-300 dark:border-sky-700 text-sky-900 dark:text-sky-100' 
                                    : 'bg-yellow-100 dark:bg-yellow-950/30 border-yellow-300 dark:border-yellow-700 text-yellow-900 dark:text-yellow-100'
                            }`}>
                                <div className="flex items-start gap-3">
                                    <Info className="h-5 w-5 mt-0.5 flex-shrink-0" />
                                    <div className="flex-1">
                                        <div className="font-bold mb-1">{feedback.is_correct ? 'Correct !' : 'Incorrect'}</div>
                                        <ExplanationWithLinks
                                            explanation={feedback.explanation}
                                            onCourseClick={handleCourseClick}
                                        />
                                    </div>
                                </div>
                                {!feedback.is_correct && (
                                    <div className="mt-4">
                                        <AiLesson
                                            questionId={currentQ.id}
                                            selectedOptionId={selectedOption}
                                            className="w-full justify-center"
                                        />
                                    </div>
                                )}
                                <Button onClick={nextQuestion} className="mt-4 w-full bg-slate-900 dark:bg-slate-700 text-white hover:bg-slate-800 dark:hover:bg-slate-600">
                                    {currentIndex < questions.length - 1 ? 'Question suivante' : 'Terminer la série'} <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
            </main>

            {/* Dialog popup pour afficher le cours */}
            {selectedCourse && (
                <Dialog open={!!selectedCourse} onOpenChange={() => setSelectedCourse(null)}>
                    <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <BookOpen className="h-5 w-5" />
                                {selectedCourse.title}
                            </DialogTitle>
                        </DialogHeader>
                        
                        <div className="space-y-4">
                            {selectedCourse.video_url && sanitizeVideoUrl(selectedCourse.video_url) && (
                                <div className="aspect-video">
                                    <iframe 
                                        src={sanitizeVideoUrl(selectedCourse.video_url)} 
                                        className="w-full h-full rounded-lg" 
                                        allowFullScreen
                                        title={selectedCourse.title}
                                    />
                                </div>
                            )}
                            
                            {selectedCourse.content && (
                                <div className="prose dark:prose-invert max-w-none">
                                    <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedCourse.content) }} />
                                </div>
                            )}

                            {selectedCourse.pdf_url && (
                                <a 
                                    href={selectedCourse.pdf_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-blue-600 hover:underline"
                                >
                                    <BookOpen className="h-4 w-4" />
                                    Télécharger le support PDF
                                </a>
                            )}
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}
